import React, { useCallback, useEffect, useRef, useState } from "react";
import {
  Row,
  Col,
  Table,
  Tag,
  Typography,
  Space,
  Statistic,
  DatePicker,
  Segmented,
  Empty,
  Tooltip,
} from "antd";
import {
  ComposedChart,
  Area,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip as RechartsTooltip,
  Legend,
  ResponsiveContainer,
} from "recharts";
import {
  DollarOutlined,
  RiseOutlined,
  FallOutlined,
  PercentageOutlined,
  FundOutlined,
  ReloadOutlined,
  ApiOutlined,
  TeamOutlined,
} from "@ant-design/icons";
import { Card, Skeleton, Button, PageHeader } from "../../components/Common";
import { useMutations } from "../../hooks/useApiMutation";
import dayjs from "dayjs";

const { Text } = Typography;
const { RangePicker } = DatePicker;

const ACCENT = {
  blue: { bg: "rgba(61,126,245,0.10)", color: "var(--accent-blue)" },
  violet: { bg: "rgba(107,78,230,0.10)", color: "var(--accent-violet)" },
  teal: { bg: "rgba(0,184,158,0.10)", color: "var(--accent-teal)" },
  amber: { bg: "rgba(224,148,26,0.10)", color: "var(--accent-amber)" },
  rose: { bg: "rgba(232,72,96,0.10)", color: "var(--accent-rose)" },
};

const eur = (v) =>
  `${Number(v ?? 0).toLocaleString(undefined, {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })} €`;

const num = (v) => Number(v ?? 0).toLocaleString();

const marginAccent = (v) => (Number(v ?? 0) >= 0 ? "teal" : "rose");
const marginColor = (v) =>
  Number(v ?? 0) >= 0 ? "var(--accent-teal)" : "var(--accent-rose)";

const StatCard = ({ icon, label, value, accent = "blue", loading }) => {
  const { bg, color } = ACCENT[accent] ?? ACCENT.blue;
  return (
    <Card style={{ height: "100%" }}>
      <div
        style={{
          display: "flex",
          alignItems: "flex-start",
          justifyContent: "space-between",
          gap: 12,
        }}
      >
        <div style={{ flex: 1, minWidth: 0 }}>
          <Text
            style={{
              fontSize: 11,
              fontWeight: 600,
              letterSpacing: "0.06em",
              textTransform: "uppercase",
              color: "var(--text-muted)",
              display: "block",
              marginBottom: 8,
            }}
          >
            {label}
          </Text>
          {loading ? (
            <Skeleton rows={1} />
          ) : (
            <Statistic
              value={value}
              valueStyle={{
                fontSize: 24,
                fontWeight: 700,
                color: "var(--text-primary)",
                fontFamily: "'Montserrat', sans-serif",
                lineHeight: 1.2,
              }}
            />
          )}
        </div>
        <div
          style={{
            width: 44,
            height: 44,
            borderRadius: 12,
            background: bg,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            fontSize: 18,
            color,
            flexShrink: 0,
          }}
        >
          {icon}
        </div>
      </div>
    </Card>
  );
};

const breakdownColumns = (keyTitle) => [
  {
    title: keyTitle,
    dataIndex: "key",
    key: "key",
    render: (v, r) => (
      <Space direction="vertical" size={0}>
        <Text strong style={{ fontSize: 13 }}>
          {v || "—"}
        </Text>
        {r.subscriptionId != null && (
          <Text type="secondary" style={{ fontSize: 11 }}>
            #{r.subscriptionId}
          </Text>
        )}
      </Space>
    ),
  },
  {
    title: "Requests",
    dataIndex: "requests",
    key: "requests",
    align: "right",
    sorter: (a, b) => a.requests - b.requests,
    render: num,
  },
  {
    title: "Billed Tokens",
    dataIndex: "tokensBilled",
    key: "tokensBilled",
    align: "right",
    sorter: (a, b) => a.tokensBilled - b.tokensBilled,
    render: num,
  },
  {
    title: "Cost",
    dataIndex: "costEur",
    key: "costEur",
    align: "right",
    sorter: (a, b) => a.costEur - b.costEur,
    render: (v) => <Text style={{ color: "var(--accent-amber)" }}>{eur(v)}</Text>,
  },
  {
    title: (
      <Tooltip title="Provider spend of Client AI agents — paid by the client on their own account, so it is not in Cost or Margin">
        Client AI spend
      </Tooltip>
    ),
    dataIndex: "clientAiCostEur",
    key: "clientAiCostEur",
    align: "right",
    sorter: (a, b) => (a.clientAiCostEur || 0) - (b.clientAiCostEur || 0),
    render: (v) =>
      Number(v) > 0 ? <Text type="secondary">{eur(v)}</Text> : <Text type="secondary">—</Text>,
  },
  {
    title: "Billed to client",
    dataIndex: "revenueEur",
    key: "revenueEur",
    align: "right",
    sorter: (a, b) => a.revenueEur - b.revenueEur,
    render: (v) => <Text style={{ color: "var(--accent-blue)" }}>{eur(v)}</Text>,
  },
  {
    title: "Margin",
    dataIndex: "marginEur",
    key: "marginEur",
    align: "right",
    defaultSortOrder: "descend",
    sorter: (a, b) => a.marginEur - b.marginEur,
    render: (v) => (
      <Text strong style={{ color: marginColor(v) }}>
        {eur(v)}
      </Text>
    ),
  },
  {
    title: "Margin %",
    dataIndex: "marginPercent",
    key: "marginPercent",
    align: "right",
    sorter: (a, b) => a.marginPercent - b.marginPercent,
    render: (v) => (
      <Tag color={Number(v) >= 0 ? "green" : "red"}>
        {Number(v ?? 0).toFixed(1)}%
      </Tag>
    ),
  },
];

const CostMarginReport = () => {
  const { mutate } = useMutations();
  const mutateRef = useRef(mutate);
  mutateRef.current = mutate;

  const [range, setRange] = useState([dayjs().subtract(29, "day"), dayjs()]);
  const [loading, setLoading] = useState(true);
  const [report, setReport] = useState(null);
  const [breakdown, setBreakdown] = useState("provider");

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [from, to] = range || [];
      const { data } = await mutateRef.current("fetchCostMargin", {
        method: "get",
        endpoint: "/v1/admin/reports/cost-margin",
        params: {
          from: from ? from.format("YYYY-MM-DD") : undefined,
          to: to ? to.format("YYYY-MM-DD") : undefined,
        },
      });
      setReport(data?.data || null);
    } catch {
      setReport(null);
    } finally {
      setLoading(false);
    }
  }, [range]);

  useEffect(() => {
    load();
  }, [load]);

  const totals = report?.totals || {};
  const series = (report?.series || []).map((p) => ({
    date: dayjs(p.date).format("DD MMM"),
    cost: p.costEur,
    revenue: p.revenueEur,
    margin: p.marginEur,
  }));
  const rows =
    breakdown === "provider"
      ? report?.byProvider || []
      : report?.bySubscription || [];
  const unbilled = report?.unbilled || [];
  const agentKeyMismatches = report?.agentKeyMismatches || [];

  return (
    <Space direction="vertical" size={20} style={{ width: "100%" }}>
      <PageHeader
        className="page-header--flush"
        title="Cost & Margin"
        subtitle={
          <>
            What the provider bills us vs. what we bill the client — per call, from the usage
            ledger (cost + markup packages: cost × markup; token packages: tokens × token value)
            {report?.euroPerToken != null && (
              <> · {Number(report.euroPerToken).toFixed(6)} €/token</>
            )}
          </>
        }
        extra={
          <>
            <RangePicker
              value={range}
              onChange={(v) => setRange(v)}
              allowClear={false}
              format="DD MMM YYYY"
            />
            <Button variant="ghost" icon={<ReloadOutlined />} onClick={load}>
              Refresh
            </Button>
          </>
        }
      />

      <Row gutter={[16, 16]}>
        <Col xs={24} sm={12} xl={6}>
          <StatCard
            icon={<DollarOutlined />}
            label="Cost (provider spend)"
            value={eur(totals.costEur)}
            accent="amber"
            loading={loading}
          />
        </Col>
        <Col xs={24} sm={12} xl={6}>
          <StatCard
            icon={<RiseOutlined />}
            label="Billed to clients"
            value={eur(totals.revenueEur)}
            accent="blue"
            loading={loading}
          />
        </Col>
        <Col xs={24} sm={12} xl={6}>
          <StatCard
            icon={Number(totals.marginEur) >= 0 ? <FundOutlined /> : <FallOutlined />}
            label="Margin"
            value={eur(totals.marginEur)}
            accent={marginAccent(totals.marginEur)}
            loading={loading}
          />
        </Col>
        <Col xs={24} sm={12} xl={6}>
          <StatCard
            icon={<PercentageOutlined />}
            label="Margin %"
            value={`${Number(totals.marginPercent ?? 0).toFixed(1)}%`}
            accent={marginAccent(totals.marginEur)}
            loading={loading}
          />
        </Col>
      </Row>

      <Card
        title={
          <Space>
            <RiseOutlined />
            <Text strong style={{ fontSize: 14 }}>
              Cost · Revenue · Margin over time
            </Text>
          </Space>
        }
        padding="md"
      >
        {loading ? (
          <Skeleton rows={4} />
        ) : series.length ? (
          <ResponsiveContainer width="100%" height={300}>
            <ComposedChart
              data={series}
              margin={{ top: 8, right: 8, left: 0, bottom: 0 }}
            >
              <defs>
                <linearGradient id="revFill" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%" stopColor="var(--accent-blue)" stopOpacity={0.4} />
                  <stop offset="95%" stopColor="var(--accent-blue)" stopOpacity={0} />
                </linearGradient>
                <linearGradient id="costFill" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%" stopColor="var(--accent-amber)" stopOpacity={0.35} />
                  <stop offset="95%" stopColor="var(--accent-amber)" stopOpacity={0} />
                </linearGradient>
              </defs>
              <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.06)" />
              <XAxis
                dataKey="date"
                tick={{ fontSize: 11, fill: "var(--text-muted)" }}
                minTickGap={24}
              />
              <YAxis
                tick={{ fontSize: 11, fill: "var(--text-muted)" }}
                width={64}
                tickFormatter={(v) => `${Number(v).toLocaleString()} €`}
              />
              <RechartsTooltip
                formatter={(v, name) => [eur(v), name]}
                contentStyle={{
                  background: "var(--surface-raised, #1a1d24)",
                  border: "1px solid rgba(255,255,255,0.08)",
                  borderRadius: 8,
                  fontSize: 12,
                }}
              />
              <Legend wrapperStyle={{ fontSize: 12 }} />
              <Area
                type="monotone"
                name="Revenue"
                dataKey="revenue"
                stroke="var(--accent-blue)"
                strokeWidth={2}
                fill="url(#revFill)"
              />
              <Area
                type="monotone"
                name="Cost"
                dataKey="cost"
                stroke="var(--accent-amber)"
                strokeWidth={2}
                fill="url(#costFill)"
              />
              <Line
                type="monotone"
                name="Margin"
                dataKey="margin"
                stroke="var(--accent-teal)"
                strokeWidth={2}
                dot={false}
              />
            </ComposedChart>
          </ResponsiveContainer>
        ) : (
          <Empty description="No usage recorded in this window" />
        )}
      </Card>

      <Card
        title={
          <Space>
            {breakdown === "provider" ? <ApiOutlined /> : <TeamOutlined />}
            <Text strong style={{ fontSize: 14 }}>
              Breakdown
            </Text>
          </Space>
        }
        extra={
          <Segmented
            size="small"
            value={breakdown}
            onChange={setBreakdown}
            options={[
              { label: "By provider", value: "provider" },
              { label: "By client", value: "subscription" },
            ]}
          />
        }
        padding="sm"
      >
        {loading ? (
          <Skeleton rows={4} />
        ) : rows.length ? (
          <Table
            rowKey={(r) =>
              breakdown === "provider"
                ? r.key
                : `${r.subscriptionId}-${r.key}`
            }
            columns={breakdownColumns(
              breakdown === "provider" ? "Provider" : "Client"
            )}
            dataSource={rows}
            size="small"
            scroll={{ x: "max-content" }}
            pagination={{ pageSize: 10, showSizeChanger: true }}
          />
        ) : (
          <Empty description="No data for this range" />
        )}
      </Card>

      {/* T-2707: successful calls that left no usage row - provider spend the table above cannot see. */}
      {!loading && unbilled.length > 0 && (
        <Card
          title={
            <Space>
              <FallOutlined />
              <Text strong style={{ fontSize: 14 }}>
                Successful calls without recorded usage
              </Text>
            </Space>
          }
          padding="md"
        >
          <Text type="secondary" style={{ display: "block", marginBottom: 12 }}>
            These calls returned 2xx on a metered endpoint but have no usage row, so their cost is
            missing from the figures above. Replay = idempotent repeat (already billed once);
            NoUsage = the response carried no token count; WriteFailed = the ledger write failed;
            Untracked = before the reason was recorded.
          </Text>
          <Table
            rowKey={(r) => `${r.subscriptionId}-${r.key}-${r.billingStatus}`}
            columns={[
              { title: "Client", dataIndex: "key", key: "key" },
              {
                title: "Reason",
                dataIndex: "billingStatus",
                key: "billingStatus",
                render: (v) => <Tag color={v === "Replay" ? "default" : "red"}>{v}</Tag>,
              },
              { title: "Calls", dataIndex: "requests", key: "requests", align: "right" },
              {
                title: "Last",
                dataIndex: "lastAtUtc",
                key: "lastAtUtc",
                render: (v) => (v ? dayjs(v).format("DD MMM YYYY HH:mm") : "—"),
              },
            ]}
            dataSource={unbilled}
            size="small"
            pagination={false}
          />
        </Card>
      )}

      {/* T-2892: agent keys billed to a different subscription than the one they were issued for. */}
      {!loading && agentKeyMismatches.length > 0 && (
        <Card
          title={
            <Space>
              <ApiOutlined />
              <Text strong style={{ fontSize: 14 }}>
                Agent keys billed to another subscription
              </Text>
            </Space>
          }
          padding="md"
        >
          <Text type="secondary" style={{ display: "block", marginBottom: 12 }}>
            These agent keys were called with an X-Api-Key of a different subscription, so the calls
            were billed to that X-Api-Key&apos;s client instead of the client the key was issued for.
            Fix each one (issue the key on the right subscription or use the matching X-Api-Key) -
            an empty list is the condition for turning on Features:EnforceAgentKeySubscription (403).
          </Text>
          <Table
            rowKey={(r) => `${r.agentApiKeyId}-${r.billedSubscriptionId}`}
            columns={[
              {
                title: "Agent key",
                key: "key",
                render: (_, r) => (
                  <Space direction="vertical" size={0}>
                    <Text code>{r.keyPrefix}…</Text>
                    <Text type="secondary" style={{ fontSize: 12 }}>
                      {r.agentName}
                      {r.label ? ` · ${r.label}` : ""}
                    </Text>
                  </Space>
                ),
              },
              {
                title: "Issued for",
                key: "keyClient",
                render: (_, r) => `${r.keyClient} (#${r.keySubscriptionId})`,
              },
              {
                title: "Billed to",
                key: "billedClient",
                render: (_, r) => (
                  <Tag color="orange">{`${r.billedClient} (#${r.billedSubscriptionId})`}</Tag>
                ),
              },
              { title: "Calls", dataIndex: "requests", key: "requests", align: "right" },
              {
                title: "Tokens billed",
                dataIndex: "tokensBilled",
                key: "tokensBilled",
                align: "right",
                render: (v) => (v ?? 0).toLocaleString(),
              },
              {
                title: "Last",
                dataIndex: "lastAtUtc",
                key: "lastAtUtc",
                render: (v) => (v ? dayjs(v).format("DD MMM YYYY HH:mm") : "—"),
              },
            ]}
            dataSource={agentKeyMismatches}
            size="small"
            pagination={false}
          />
        </Card>
      )}
    </Space>
  );
};

export default CostMarginReport;
